<template>
  <aside class="implementation-plan-page">
    <DocumentPageTitle
      :word-count="totalWords"
      :word-limit="wordLimit"
      title="Implementation Plan"
    />

    <div
      v-for="storeItem in storeItems"
      :id="storeItem.id"
      :key="storeItem.id"
      class="mb-8"
    >
      <template v-if="storeItem.type === 'editor'">
        <TextEditorInput
          :id="`${storeItem.id}-editor`"
          v-model="planContentKey[storeItem.storeKey].text"
          :label="storeItem.label"
          :word-limit="wordLimit"
          :total-word-count="totalWords"
          @update:word-count="handleWordCountUpdate"
        />

        <DocumentAnnexureUploadButton :store-key="`implementationPlan.${storeItem.storeKey}.documents`" />
      </template>

      <template v-else-if="storeItem.type === 'municipal-block'">
        <h2>
          {{ storeItem.label }}
        </h2>

        <div class="light-chrome-banner">
          <strong>Please Note:</strong> Please ensure you have created a Section first. Projects must be assigned to a section to be saved. Before clicking save, double-check that your project is categorized correctly.
        </div>

        <div
          v-if="planContentKey.municipalProgrammesAndProjects.length > 0"
          class="border-b border-chrome-light border-dashed w-full mb-4 pb-4"
        >
          <DefaultSearch
            class="p-0 mb-4 [&_.input-field]:!min-w-[420px] [&_form]:!gap-0"
            btn-class="btn-solid btn-primary !rounded-none"
            placeholder="Type to search projects/programmes"
            :archiveable="false"
            @search="searchParams = $event"
          />

          <template v-if="filteredSections.length > 0">
            <template
              v-for="section in filteredSections"
              :key="section.id"
            >
              <AppTable class="max-w-[640px]">
                <AppTableHeader>
                  <th class="!border-r-0 font-bold">
                    {{ section.sectionName }}
                  </th>

                  <th>
                    <DocumentActions
                      subject="section"
                      @edit-clicked="formModal.open('section', section)"
                      @delete-clicked="deleteModal.open('section', section)"
                    />
                  </th>
                </AppTableHeader>

                <AppTableBody
                  :loading="planStore.loading"
                  :colspan="2"
                  :no-results="section.projects.length === 0"
                  no-results-text="No projects yet"
                >
                  <tr
                    v-for="(project, index) in section.projects"
                    :key="index"
                  >
                    <td class="!border-r-0">
                      <span
                        class="line-clamp-2"
                        v-html="projectMarkup(project)"
                      />
                    </td>

                    <td>
                      <DocumentActions
                        subject="project"
                        @edit-clicked="formModal.open('project', project, { sectionId: section.id })"
                        @delete-clicked="deleteModal.open('project', project, { sectionId: section.id })"
                      />
                    </td>
                  </tr>
                </AppTableBody>
              </AppTable>

              <AppButton
                class="btn-outline btn-secondary border-dashed !p-2 mb-4 rounded !font-normal"
                @click="formModal.open('project', projectSchema, { sectionId: section.id })"
              >
                <FontAwesomeIcon :icon="faPlus" />
                Add a project
              </AppButton>
            </template>
          </template>

          <div v-else>
            No results match your search
          </div>
        </div>

        <AppButtonGroup>
          <AppButton
            class="btn-outline btn-secondary !p-2 rounded !font-normal"
            @click="formModal.open('section', sectionSchema)"
          >
            <FontAwesomeIcon :icon="faPlus" />
            Add a new section
          </AppButton>

          <AppButton
            v-if="planContentKey.municipalProgrammesAndProjects.length === 0"
            class="btn-outline !border-editor-chrome-light disabled !p-2 rounded !font-normal"
            :disabled="true"
          >
            <FontAwesomeIcon :icon="faPlus" />
            Add a project
          </AppButton>

          <AppButton
            class="btn-outline btn-secondary !p-2 rounded !font-normal"
            @click="showBatchImport = true"
          >
            <FontAwesomeIcon :icon="faUpload" />
            Batch upload
          </AppButton>
        </AppButtonGroup>
      </template>
    </div>

    <AppModal
      v-if="!!formModal.type"
      class="[&_.modal-body]:!p-0 [&_.modal-body]:!overflow-auto"
      :title="`${formModal.isEditing ? 'Edit' : 'Add'} a ${formModal.type}`"
      :size="formModal.type === 'section' ? 'xl' : '5xl'"
      :disabled="formModal.processing"
      @close="handleCancel"
    >
      <template #body>
        <SectionForm
          v-if="formModal.type === 'section'"
          v-model="formModal.data"
          class="p-8"
          :error="sectionError"
          @submit="handleSave"
        />

        <ProjectForm
          v-if="formModal.type === 'project'"
          v-model="formModal.data"
          :error="projectError"
          @submit="handleSave"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @submit-clicked="handleSave"
          @cancel-clicked="handleCancel"
        />
      </template>
    </AppModal>

    <ConfirmationModal
      v-if="!!deleteModal.type"
      intent="danger"
      :title="`Delete ${deleteModal.type}`"
      :processing="deleteModal.processing"
      @close="deleteModal.close"
      @confirm="handleDelete"
    >
      <span class="block">Are you sure you want to delete "<strong>{{ deleteLabel }}</strong>"?</span>

      <span
        v-if="deletingPopulatedSection"
        class="block"
      >
        This section contains projects. Deleting it will permanently remove the project and
        <strong>all projects assigned to it.</strong>
        This action cannot be undone.
      </span>

      <span
        v-else
        class="block"
      >
        This action cannot be undone.
      </span>
    </ConfirmationModal>

    <BatchImportModal
      v-if="showBatchImport"
      :definition="projectsImport"
      @close="showBatchImport = false"
    />
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue';
import { usePlanStore } from '@/store';
import {
  DocumentPageTitle,
  DocumentActions,
  DocumentAnnexureUploadButton,
} from '@/components/plan-document/components';

import { useDomPurify, useModal, useString, useTinyMCEWordCount } from '@/composables';
import { projectsImport } from '@/composables/import';
import { faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { AppButton, AppButtonGroup, AppModal } from '@/components/app';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { SectionForm, ProjectForm } from '@/components/plan-document/forms';
import { BatchImportModal } from '@/components/plan-document/import';
import { ConfirmationModal } from '@/components/modals';
import { ButtonSubmitRow } from '@/components/buttons';
import { DefaultSearch } from '@/components/forms/search';
import { TextEditorInput } from '@/components/form-inputs';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

const planStore = usePlanStore();
const string = useString();
const domPurify = useDomPurify();

const wordLimit = 15000;
const sectionError = ref(null);
const projectError = ref({
  description: null,
  developmentStrategyId: null,
});

const planContentKey = computed(() => {
  return planStore.content?.implementationPlan || {};
});

const storeItems = [
  { label: 'Overview of implementation plan', storeKey: 'overview', type: 'editor' },
  { label: 'Municipal programmes and projects', type: 'municipal-block' },
  { label: 'Programmes and projects of other organs of state', storeKey: 'otherProgrammesAndProjects', type: 'editor' },
  { label: 'Local Economic Development', storeKey: 'localEconomicDevelopment', type: 'editor' },
  { label: 'Disaster management plan', storeKey: 'disasterManagementPlan', type: 'editor' },
  { label: 'Climate change response implementation plan', storeKey: 'climateChangeResponse', type: 'editor' },
].map(storeItem => ({ ...storeItem, id: string.sluggify(storeItem.label) }));

const editorStoreItemIds = storeItems
  .filter(s => s.type === 'editor')
  .map(s => `${s.id}-editor`);

const {
  totalWords,
  updateCount,
} = useTinyMCEWordCount(wordLimit, editorStoreItemIds);

const handleWordCountUpdate = ({ id, count }) => {
  updateCount(id, count);
};

const searchParams = ref({
  search: null,
});

// modal shit
const formModal = useModal();
const deleteModal = useModal();

const showBatchImport = ref(false);

const sectionSchema = { id: null, sectionName: null };
const projectSchema = {
  id: null,
  description: null,
  developmentStrategyId: null,
  wardLocation: null,
  latitude: null,
  longitude: null,
  totalProjectValue: null,
  budget: {
    capital: { year1: null, year2: null, year3: null },
  },
};

const projectLabel = (project) => string.stripHtml(project.description) || 'Untitled project';

const projectMarkup = (project) => project.description
  ? domPurify.sanitize(project.description)
  : 'Untitled project';

const deleteLabel = computed(() => {
  const { type, data } = deleteModal;

  return type === 'section' ? data.sectionName : projectLabel(data);
});

// Only a section that still holds projects warns about taking them down with it
const deletingPopulatedSection = computed(() => {
  const { type, data } = deleteModal;

  return type === 'section' && data.projects?.length > 0;
});

const filteredSections = computed(() => {
  const query = searchParams.value.search?.toLowerCase().trim();
  const sections = planContentKey.value.municipalProgrammesAndProjects;

  // If search is empty, return the original list from the store
  if (!query) return sections;

  // Use reduce to build a new array of sections that match criteria
  return sections.reduce((acc, section) => {
    // Check if the Section Name matches
    const sectionNameMatches = section.sectionName?.toLowerCase().includes(query);

    // Check if any Projects inside the section match
    const matchingProjects = section.projects.filter(project =>
      projectLabel(project).toLowerCase().includes(query),
    );

    // If Section Name matches: Return section with ALL projects (context is key).
    // If Section doesn't match, but Projects do: Return section with ONLY matching projects.
    if (sectionNameMatches) {
      acc.push(section);
    } else if (matchingProjects.length > 0) {
      // Create a shallow copy of the section with the filtered projects array
      // This ensures we don't mutate the store
      acc.push({
        ...section,
        projects: matchingProjects,
      });
    }

    return acc;
  }, []);
});

const handleSave = () => {
  const { type, data, isEditing, context } = formModal;
  formModal.processing = true;

  if (type === 'section') {
    if (!data.sectionName) {
      sectionError.value = 'This is a required field';

      formModal.processing = false;
      return;
    }

    const sections = planContentKey.value.municipalProgrammesAndProjects;

    if (isEditing) {
      const index = sections.findIndex(s => s.id === data.id);
      if (index !== -1) sections[index] = { ...data, projects: sections[index].projects };
    } else {
      sections.push({ ...data, id: Date.now(), projects: [] });
    }
  }
  else if (type === 'project') {
    // An "empty" TinyMCE field still holds markup, so test the text it carries
    const hasDescription = !!string.stripHtml(data.description);

    if (!hasDescription || !data.developmentStrategyId) {
      if (!hasDescription) {
        projectError.value.description = 'This is a required field';
      }

      if (!data.developmentStrategyId) {
        projectError.value.developmentStrategyId = 'This is a required field';
      }
      formModal.processing = false;
      return;
    }

    const parentSection = planContentKey.value.municipalProgrammesAndProjects
      .find(s => s.id === context.sectionId);

    if (parentSection) {
      if (isEditing) {
        const index = parentSection.projects.findIndex(p => p.id === data.id);
        if (index !== -1) parentSection.projects[index] = { ...data };
      } else {
        parentSection.projects.push({ ...data, id: Date.now() });
      }
    }
  }

  formModal.close();
  sectionError.value = null;
  projectError.value = {
    description: null,
    developmentStrategyId: null,
  };
};

const handleDelete = () => {
  const { type, data, context } = deleteModal;
  deleteModal.processing = true;

  if (type === 'section') {
    planContentKey.value.municipalProgrammesAndProjects = planContentKey.value.municipalProgrammesAndProjects
      .filter(s => s.id !== data.id);
  }
  else if (type === 'project') {
    const parentSection = planContentKey.value.municipalProgrammesAndProjects
      .find(s => s.id === context.sectionId);

    if (parentSection) {
      parentSection.projects = parentSection.projects
        .filter(p => p.id !== data.id);
    }
  }

  deleteModal.close();
};

const handleCancel = () => {
  sectionError.value = null;

  projectError.value = {
    description: null,
    developmentStrategyId: null,
  };

  formModal.close();
};

</script>
